import React, { useState } from "react";
import { Avatar, Button, ButtonGroup, CircularProgress, Dialog, IconButton, Menu, MenuItem, Pagination, Stack, Typography } from "@mui/material";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Box } from "@mui/material";
import { useWarehouse } from "../../hooks/useWarehouse";
import LoadingIndicator from "@shared/ui/common/LoadingIndicator";
import { Paper } from "@shared/ui/common/paper";
import WarehouseInventoryCount from "./WarehouseInventoryCount";
import { InfoActionMessage } from "@shared/ui/common/InfoActionMessage";
import { useNavigate } from "react-router-dom";
import { Icon } from "@shared/icons";
import { useWarehouseConnection } from "../../hooks/useWarehouseConnection";

interface WarehouseInventoryProps {
    warehouseId: string;
}

const WarehouseInventory = ({ warehouseId }: WarehouseInventoryProps) => {

    const [showArchived, setShowArchived] = useState(false);
    const [showPurchasable, setShowPurchasable] = useState(true);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const { inventory, isLoading, page, setPage, totalPages } = useWarehouse(warehouseId, { active: !showArchived, purchasable: showPurchasable });
    const { warehouseConnections } = useWarehouseConnection(warehouseId);


    const navigate = useNavigate();

    const [open, setOpen] = useState(false);

    const handleClose = () => {
        setOpen(false);
    }

    return (
        <Box sx={{ flex: 3 }}>
            <Paper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider', p: 2, alignItems: 'center' }}>
                    <Typography variant="body1">Inventory</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>


                        {!warehouseConnections || warehouseConnections.length === 0 && (
                            <Button variant="outlined" color="primary" onClick={() => setOpen(true)}>Count Inventory</Button>
                        )}

                        <IconButton
                            onClick={(event) => setAnchorEl(anchorEl ? null : event.currentTarget as HTMLElement)}
                        >
                            <Icon icon="DotVertical" />
                        </IconButton>
                    </Box>
                </Box>

                <Menu
                    anchorEl={anchorEl}
                    open={!!anchorEl}
                    onClose={() => setAnchorEl(null)}
                >
                    <MenuItem disableRipple>
                        <ButtonGroup variant="outlined">
                            <Button
                                onClick={() => setShowPurchasable(true)}
                                color='info'
                                variant={showPurchasable ? 'contained' : 'outlined'}
                                size='small'
                            >
                                Purchasable
                            </Button>
                            <Button
                                onClick={() => setShowPurchasable(false)}
                                color='info'
                                variant={!showPurchasable ? 'contained' : 'outlined'}
                                size='small'
                            >
                                Assembled
                            </Button>

                        </ButtonGroup>
                    </MenuItem>

                    <MenuItem disableRipple>
                        <ButtonGroup variant="outlined">
                            <Button
                                onClick={() => setShowArchived(true)}
                                color='info'
                                variant={showArchived ? 'contained' : 'outlined'}
                                size='small'
                            >
                                Archived
                            </Button>
                            <Button
                                onClick={() => setShowArchived(false)}
                                color='info'
                                variant={!showArchived ? 'contained' : 'outlined'}
                                size='small'
                            >
                                Active
                            </Button>

                        </ButtonGroup>
                    </MenuItem>

                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            navigate(`/inventory/warehouses/${warehouseId}/mutations`);
                        }}

                    >
                        <Stack direction="row" alignItems="center" justifyContent="space-between" flex={1}>
                            <Typography>View Mutations</Typography>
                            <Icon icon="ChevronRight" />
                        </Stack>
                    </MenuItem>
                </Menu>




                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Product</TableCell>
                                <TableCell align="right">Quantity</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={2}>
                                        <Stack p={4} direction="row" justifyContent="center" alignItems="center">
                                            <CircularProgress />
                                        </Stack>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                <>
                                    {inventory && inventory.length > 0 && inventory.map((item) => (
                                        <TableRow key={item.product.id}>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                    <Avatar
                                                        src={item.product.image}
                                                        alt={item.product.title}
                                                        variant="rounded"
                                                        sx={{ width: 40, height: 40, border: 1, borderColor: 'divider' }}
                                                    />
                                                    <Typography>{item.product.title}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography>{item.quantity}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {(!inventory || inventory.length === 0) && (
                                        <TableRow>
                                            <TableCell colSpan={2}>
                                                <InfoActionMessage
                                                    title="No inventory items found."
                                                    description="You can manually add your inventory numbers or connect this warehouse to a third party."
                                                    buttonText="Manually add your inventory"
                                                    onButtonClick={() => setOpen(true)}
                                                />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </>
                            )}
                        </TableBody>
                    </Table>

                    {inventory && (

                        <Stack p={2} direction="row" justifyContent="center" alignItems="center">   <Pagination
                            count={totalPages || 0}
                            page={page}
                            onChange={(_, value) => setPage(value)}
                            color="primary"
                            size="large"
                            shape="rounded"
                            showFirstButton
                            showLastButton
                        /></Stack>
                    )}
                </TableContainer>
            </Paper>

            <WarehouseInventoryCount
                open={open}
                onSubmit={handleClose}
                onClose={handleClose}
                warehouseId={warehouseId}
            />
        </Box>
    )
}

export default WarehouseInventory;